import { MapPin, ArrowUpRight, Clock, Phone } from "lucide-react";
import { SHOWROOMS } from "@/data/showrooms";

function Frame({ title, note, children }) {
  return (
    <section className="mb-24">
      <div className="mb-6">
        <h2 className="font-serif text-3xl font-light tracking-tight">{title}</h2>
        <p className="mt-2 text-sm font-light text-muted-foreground">{note}</p>
      </div>
      <div className="border border-foreground/20 overflow-hidden">{children}</div>
    </section>
  );
}

const OptionTopBar = () => (
  <div className="bg-primary text-primary-foreground py-3 px-6">
    <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-1 text-[11px] tracking-[0.18em] uppercase">
      {SHOWROOMS.map((s) => (
        <span key={s.id} className="inline-flex items-center gap-2">
          <MapPin className="w-3 h-3 text-[#E23B4E]" />
          <span className="text-primary-foreground/60">{s.label}:</span> {s.name}
        </span>
      ))}
    </div>
  </div>
);

const OptionFooterBlock = () => (
  <div className="bg-primary text-primary-foreground p-10 md:p-14">
    <p className="text-xs tracking-[0.25em] uppercase text-primary-foreground/50 mb-6">Our showrooms</p>
    <div className="grid md:grid-cols-2 gap-10">
      {SHOWROOMS.map((s) => (
        <div key={s.id}>
          <p className="font-serif text-xl mb-2">{s.name}</p>
          <p className="text-sm font-light text-primary-foreground/70 leading-relaxed flex items-start gap-2.5">
            <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-[#E23B4E]" /> {s.address}
          </p>
        </div>
      ))}
    </div>
  </div>
);

const OptionCards = () => (
  <div className="bg-background p-10 md:p-14">
    <p className="text-xs tracking-[0.3em] uppercase text-secondary-foreground mb-3">Visit us</p>
    <h3 className="font-serif text-3xl font-light tracking-tight mb-10">
      Two showrooms, <span className="italic">one obsession.</span>
    </h3>
    <div className="grid md:grid-cols-2 gap-6">
      {SHOWROOMS.map((s) => (
        <div key={s.id} className="border border-border bg-card/60 p-8">
          <p className="text-xs tracking-[0.25em] uppercase text-secondary-foreground mb-3">{s.label}</p>
          <p className="font-serif text-2xl font-light tracking-tight mb-4">{s.name}</p>
          <p className="text-sm font-light text-muted-foreground leading-relaxed flex items-start gap-2.5">
            <MapPin className="w-4 h-4 mt-0.5 shrink-0" /> {s.address}
          </p>
          <p className="mt-4 text-sm font-light text-muted-foreground flex items-center gap-2.5">
            <Clock className="w-4 h-4 shrink-0" /> Open daily till 7:00 PM
          </p>
          <a
            href={s.link}
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-6 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase"
          >
            <span className="border-b border-foreground/40 pb-1 transition-colors duration-300 group-hover:border-foreground">
              Get directions
            </span>
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>
      ))}
    </div>
  </div>
);

const NavbarPillMock = () => (
  <div className="bg-background">
    <div className="flex items-center justify-between px-6 h-16 border-b border-border">
      <span className="font-serif text-xl tracking-tight">Varun <span className="italic font-light">Marbles</span></span>
      <span className="flex items-center gap-3">
        <span className="inline-flex items-center gap-2 border border-border px-3 sm:px-4 py-2 text-[10px] tracking-[0.18em] uppercase text-secondary-foreground">
          <MapPin className="w-3 h-3 text-[#C8102E]" /> 2 showrooms in Gurgaon
        </span>
        <span className="bg-primary text-primary-foreground text-[10px] tracking-[0.2em] uppercase px-5 py-2.5">Enquire</span>
      </span>
    </div>
    <div className="bg-secondary/50 h-32 flex items-center justify-center text-xs tracking-[0.25em] uppercase text-secondary-foreground">
      Page content below
    </div>
  </div>
);

const HeroLineMock = () => (
  <div className="bg-background p-10 md:p-14">
    <p className="text-xs tracking-[0.3em] uppercase text-secondary-foreground mb-5">
      Varun Marbles — Purveyors of fine stone
    </p>
    <p className="font-serif text-4xl md:text-5xl font-light tracking-tighter leading-[1.02] mb-8">
      The art of <span className="italic">eternal</span> stone.
    </p>
    <div className="flex flex-wrap gap-4 mb-10">
      <span className="bg-primary text-primary-foreground text-[10px] tracking-[0.2em] uppercase px-6 py-3.5">Explore collections</span>
      <span className="border border-foreground/25 text-[10px] tracking-[0.2em] uppercase px-6 py-3.5">Enquire</span>
    </div>
    <p className="flex flex-wrap items-center gap-x-8 gap-y-2 text-xs tracking-[0.15em] uppercase text-secondary-foreground border-t border-border pt-6">
      {SHOWROOMS.map((s) => (
        <span key={s.id} className="inline-flex items-center gap-2">
          <MapPin className="w-3.5 h-3.5 text-[#C8102E]" /> {s.name}
        </span>
      ))}
    </p>
  </div>
);

const TopBarSplitMock = () => (
  <div className="bg-background">
    <div className="bg-primary text-primary-foreground px-6 py-2.5 flex items-center justify-between text-[10px] tracking-[0.18em] uppercase">
      {SHOWROOMS.map((s) => (
        <span key={s.id} className="inline-flex items-center gap-2">
          <MapPin className="w-3 h-3 text-[#E23B4E]" />
          <span className="text-primary-foreground/50">{s.label}:</span> {s.name}
        </span>
      ))}
    </div>
    <div className="bg-secondary/50 h-28 flex items-center justify-center text-xs tracking-[0.25em] uppercase text-secondary-foreground">
      Menu bar below
    </div>
  </div>
);

const TopBarPhoneMock = () => (
  <div className="bg-background">
    <div className="bg-primary text-primary-foreground px-6 py-2.5 flex flex-wrap items-center justify-center gap-x-6 gap-y-1 text-[10px] tracking-[0.18em] uppercase">
      <span className="inline-flex items-center gap-2">
        <MapPin className="w-3 h-3 text-[#E23B4E]" /> Gurgaon — {SHOWROOMS[0].name} · {SHOWROOMS[1].name}
      </span>
      <span className="inline-flex items-center gap-2 text-primary-foreground/60">
        <Phone className="w-3 h-3 text-[#E23B4E]" /> +91 97735 58193
      </span>
    </div>
    <div className="bg-secondary/50 h-28 flex items-center justify-center text-xs tracking-[0.25em] uppercase text-secondary-foreground">
      Menu bar below
    </div>
  </div>
);

export default function ShowroomOptions() {
  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 pt-40 pb-32" data-testid="showroom-options-page">
      <p className="text-xs tracking-[0.3em] uppercase text-secondary-foreground mb-6">Preview — not linked in the menu</p>
      <h1 className="font-serif text-5xl sm:text-6xl font-light tracking-tighter leading-[0.98] mb-6">
        Top bar <span className="italic">styles.</span>
      </h1>
      <p className="max-w-lg text-base font-light leading-relaxed text-muted-foreground mb-20">
        A slim navy top bar is now live above the menu on every page — currently Style 1 below.
        Pick a different style and I'll swap it in.
      </p>

      <Frame
        title="Style 1 — Centered (live now)"
        note="Both showrooms centered in one row, each linked to Google Maps."
      >
        <OptionTopBar />
        <div className="bg-secondary/50 h-28 flex items-center justify-center text-xs tracking-[0.25em] uppercase text-secondary-foreground">
          Menu bar below
        </div>
      </Frame>

      <Frame
        title="Style 2 — Split edges"
        note="Showroom 1 pinned left, Showroom 2 pinned right — a more architectural, balanced strip."
      >
        <TopBarSplitMock />
      </Frame>

      <Frame
        title="Style 3 — Compact with phone"
        note="One condensed line with both locations plus your phone number on the same bar."
      >
        <TopBarPhoneMock />
      </Frame>

      <Frame
        title="Header option B — Nav pill"
        note="Alternative to the top bar: a small '2 showrooms in Gurgaon' pill inside the menu bar itself, next to Enquire."
      >
        <NavbarPillMock />
      </Frame>

      <Frame
        title="Header option C — Hero showroom line"
        note="Not in the header, but on the first screen: both showrooms listed under the hero buttons."
      >
        <HeroLineMock />
      </Frame>

      <Frame
        title="Applied — Footer showroom block"
        note="This one is live in the footer on every page."
      >
        <OptionFooterBlock />
      </Frame>

      <Frame
        title="Applied — Contact page maps"
        note="Live on the Contact page: embedded Google Maps with a showroom switcher, hours and directions."
      >
        <OptionCards />
      </Frame>
    </div>
  );
}
